import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronDown, Phone, ShieldCheck, Award, MapPin } from "lucide-react";
import { DEEP_SERVICES } from "@/data/deepServices";
import { SERVICES, CITIES, BUSINESS } from "@/data/content";
import { SectionHeading, FinalCTA } from "@/components/Shared";
import { SEO, breadcrumbSchema, faqSchema, serviceSchema } from "@/components/SEO";

// HowTo schema — surfaces step lists in Google AI Overview / SGE.
const howToSchema = ({ name, steps, totalTime }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  totalTime: totalTime || "P1D",
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.text,
  })),
});

// FAQ accordion — same UX as the rest of the site
function FAQAccordion({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border-b border-ink-line">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left py-5 flex justify-between items-start gap-4"
        data-testid={`deep-faq-${q.slice(0, 20).replace(/\s/g, "-")}`}
        aria-expanded={open}
      >
        <span className="font-heading text-lg font-bold">{q}</span>
        <ChevronDown className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-5 text-ink-soft leading-relaxed">{a}</div>}
    </div>
  );
}

export function DeepServicePage() {
  const { slug } = useParams();
  const svc = DEEP_SERVICES.find((d) => d.slug === slug);
  if (!svc) return null; // ServiceDetailPage handles existing slugs; null lets router fall through

  const parent = svc.parentService ? SERVICES.find((s) => s.slug === svc.parentService) : null;
  const related = (svc.related || []).map((s) => DEEP_SERVICES.find((d) => d.slug === s)).filter(Boolean);

  // Combined schema graph — Service + FAQPage + BreadcrumbList (+ optional HowTo)
  const graph = [
    serviceSchema({ name: svc.h1, description: svc.description, path: `/services/${svc.slug}` }),
    faqSchema(svc.faqs),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
      { name: svc.keyword.replace(/\b\w/g, (c) => c.toUpperCase()), path: `/services/${svc.slug}` },
    ]),
  ];
  if (svc.howTo) graph.push(howToSchema(svc.howTo));

  return (
    <div>
      <SEO
        title={svc.title}
        description={svc.description}
        path={`/services/${svc.slug}`}
        schema={{ "@context": "https://schema.org", "@graph": graph }}
      />

      {/* Hero — exact-match H1 + 40-60-word lead optimized for AI Overview citation */}
      <section className="bg-bg-dark text-white py-16 md:py-24 relative overflow-hidden">
        <div className="container-x relative">
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Link to="/services" className="hover:text-brand">Services</Link>
            <span>/</span>
            <span className="text-white">{svc.h1.split(" in ")[0]}</span>
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tight mt-5 max-w-4xl leading-[1.02]" data-testid="deep-service-h1">
            {svc.h1}
          </h1>
          <p className="text-white/85 text-base md:text-lg max-w-3xl mt-6 leading-relaxed" data-testid="deep-service-lead">
            {svc.lead}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/get-estimate" className="btn-primary" data-testid="deep-service-estimate-cta">
              Get free estimate <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${BUSINESS.phoneTel}`} className="btn-inverted" data-testid="deep-service-phone-cta">
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
          </div>

          {/* Trust signals inline — AI Overview likes citable trust markers near the lead */}
          <div className="mt-8 flex flex-wrap gap-4 text-sm text-white/85">
            <div className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand" /> Licensed &amp; insured in Michigan</div>
            <div className="inline-flex items-center gap-2"><Award className="w-4 h-4 text-brand" /> Master Shingle Applicator (Wizard Level)</div>
            <div className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-brand" /> Oakland County since 2010</div>
          </div>
        </div>
      </section>

      {/* Body — structured Q&A sections for AI Overview snippet extraction */}
      <section className="py-16 md:py-20">
        <div className="container-x grid lg:grid-cols-3 gap-10 lg:gap-16">
          <article className="lg:col-span-2 space-y-12" data-testid="deep-service-body">
            {svc.sections.map((sec) => (
              <div key={sec.h2}>
                <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight">{sec.h2}</h2>
                <p className="mt-4 text-ink-soft leading-relaxed text-base md:text-lg">{sec.body}</p>
              </div>
            ))}

            {/* HowTo steps — rendered as <ol> so Google can map the visible content
                directly to the HowTo schema for AI Overview surfaces */}
            {svc.howTo && (
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight">{svc.howTo.name}</h2>
                <ol className="mt-6 space-y-4" data-testid="deep-service-howto">
                  {svc.howTo.steps.map((s, i) => (
                    <li key={s.name} className="flex gap-4">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-brand text-white font-heading font-extrabold flex items-center justify-center text-sm">{i + 1}</div>
                      <div>
                        <div className="font-heading font-bold text-lg">{s.name}</div>
                        <div className="text-ink-soft mt-1 leading-relaxed">{s.text}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </article>

          <aside className="lg:col-span-1">
            <div className="bg-bg-soft border border-ink-line/60 p-6 sticky top-28">
              <div className="overline text-brand">Same-day response</div>
              <div className="font-heading text-xl font-bold mt-2">Free on-site inspection</div>
              <div className="text-sm text-ink-soft mt-2 leading-relaxed">
                Written, line-item estimate within 24 hours. Insurance documentation included at no charge.
              </div>
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-primary w-full justify-center mt-5" data-testid="deep-service-sidebar-call">
                <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
              </a>
              <Link to="/get-estimate" className="btn-secondary w-full justify-center mt-3">
                Request online <ArrowRight className="w-4 h-4" />
              </Link>

              {parent && (
                <div className="mt-6 pt-6 border-t border-ink-line/60">
                  <div className="overline text-ink-soft">Part of</div>
                  <Link to={`/services/${parent.slug}`} className="font-heading text-base font-bold mt-1 block hover:text-brand">
                    {parent.name}
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ — FAQPage schema + visible accordion. Highest-yield section for AI
          Overview because Google extracts exact Q&A pairs from FAQPage schema. */}
      <section className="py-16 md:py-20 bg-bg-soft border-t border-ink-line">
        <div className="container-x grid md:grid-cols-3 gap-10">
          <div>
            <SectionHeading overline="FAQ" title={`${svc.keyword.replace(/\b\w/g, (c) => c.toUpperCase())} questions`} />
            <p className="text-sm text-ink-soft mt-4 leading-relaxed">
              The questions Michigan homeowners actually ask us. Answered by the Goral crew, not a sales script.
            </p>
          </div>
          <div className="md:col-span-2" data-testid="deep-service-faqs">
            {svc.faqs.map((f) => (
              <FAQAccordion key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Related deep services — keeps every new SEO silo internally linked */}
      {related.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="container-x">
            <SectionHeading overline="Related services" title="Other repairs we handle on the same job" />
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  className="card-base p-5 hover:border-brand transition-colors group"
                  data-testid={`deep-related-${r.slug}`}
                >
                  <div className="overline text-brand">Related</div>
                  <div className="font-heading text-lg font-bold mt-2 group-hover:text-brand transition-colors">{r.keyword.replace(/\b\w/g, (c) => c.toUpperCase())}</div>
                  <div className="text-sm text-ink-soft mt-2 leading-relaxed line-clamp-3">{r.description}</div>
                  <div className="mt-3 text-sm text-brand font-semibold inline-flex items-center gap-1">Learn more <ArrowRight className="w-4 h-4" /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cities served — internal linking to all 13 city pages so the new deep
          service page passes link equity into the service-area silo. */}
      <section className="py-16 md:py-20 bg-bg-soft border-t border-ink-line">
        <div className="container-x">
          <SectionHeading overline="Service area" title={`${svc.keyword.replace(/\b\w/g, (c) => c.toUpperCase())} across Oakland County, MI`} sub="We serve every major Oakland County community with the same crew and warranty." />
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/service-areas/${c.slug}`}
                className="px-4 py-3 border border-ink-line bg-white hover:border-brand hover:text-brand text-sm font-semibold rounded-sm transition-colors text-center"
                data-testid={`deep-city-link-${c.slug}`}
              >
                {c.name}, MI
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
